/* global React */
import { useMemo } from 'react'
import { Box, useTheme } from '@mui/material'
import type { Theme } from '@mui/material/styles'
import { getThemeColor, isStr } from 'lib/util'
import { useMemoRef, useStyles } from '@platform/react/hook'
import Image, {
  type ImageIcon,
  type ImageProps,
  type ImageShape,
} from 'ui/Element/Image'

// Avatar exposes the Image api unchanged; its `fallbackIcon` only gets
// defaults applied
export type AvatarProps = ImageProps

export interface AvatarEditProps extends AvatarProps {
  /** legacy image path, only used as a memo key */
  src?: string | null
}

interface AvatarStyleProps {
  /** shape of the avatar */
  shape: ImageShape
  /** background color behind the fallback icon */
  background?: string | null
  /** whether the attachment is a png image */
  isPng?: boolean
  /** optional fixed width of the avatar */
  width?: number | string | null
  /** optional fixed height of the avatar */
  height?: number | string | null
}

/**
 * Returns the avatar element's current theme styles.
 *
 * @param theme            the application's current theme
 * @param props            the element's properties
 * @param props.shape      shape of the avatar
 * @param props.background background color behind the fallback icon
 * @param props.isPng      whether the attachment is a png image
 * @param props.width      optional fixed width of the avatar
 * @param props.height     optional fixed height of the avatar
 * @returns                the avatar's styles
 */
const styles = (theme: Theme, { shape, background, isPng, width, height }: AvatarStyleProps) => ({
  root: {
    ...!width && !height
      ? { height: '100%', width: '100%' }
      : { width, height },
    ...background && { backgroundColor: getThemeColor(theme, background) },
    ...shape === 'circle' && {
      borderRadius: '50%',
      ...isPng && {
        padding: '0.313rem', // 5px
      },
    },
  },
})

/**
 * Avatar Image Element
 *
 * Displays the avatar of a resource: its attachment if there is one, or an
 * icon as a fallback.
 *
 * @param props              the element's properties
 * @param props.ref          a reference to the root element
 * @param props.fallbackIcon the icon to show as a fallback
 * @param props.attachment   attachment to fetch and display
 * @param props.group        attachment group of the avatar
 * @param props.value        ref of the resource to fetch the avatar for
 * @param props.api          api information of the resource to fetch
 * @param props.shape        shape of the avatar
 * @param props.width        optional fixed width of the avatar
 * @param props.height       optional fixed height of the avatar
 * @param props.fit          how to fit the image into the avatar box
 * @returns                  the new avatar element
 */
const AvatarImage = ({ ref, fallbackIcon = null, ...props }: AvatarProps) => {
  const {
    attachment,
    group,
    value,
    api = { ref: 'avatar' },
    shape = 'natural',
    rounded = false,
    width = null,
    height = null,
    fit = 'cover',
  } = props

  const theme = useTheme() as any

  const isPng = useMemo(() => attachment?.value?.mimetype?.includes('png'), [attachment])

  const {
    name = 'user',
    source = name,
    color = theme.palette.white.main,
    background = theme.palette.gray[960],
    padding,
    size = '70%',
  } = isStr(fallbackIcon) ? {} : (fallbackIcon || {}) as ImageIcon

  const classes = useStyles(styles, {
    shape, background, isPng, width, height,
  })() as Record<string, string>

  return (
    <Box className={classes.root}>
      <Image
        ref={ref}
        api={api}
        value={value}
        group={group}
        attachment={attachment}
        shape={shape}
        fit={fit}
        rounded={rounded}
        width={width}
        height={height}
        fallbackIcon={isStr(fallbackIcon)
          ? fallbackIcon
          : { name, source, color, background, padding, size }}
      />
    </Box>
  )
}

export default useMemoRef(AvatarImage, (props: AvatarProps) => [props.value])
